import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Image, Spin, Tabs } from 'antd';
import { renderAsync } from 'docx-preview';
import * as XLSX from 'xlsx';
import { resolveUploadUrl } from '../utils/uploadUrl';
import {
  friendlyDocxPreviewError,
  readBlobHead,
  sniffPreviewKindFromBytes
} from '../utils/attachmentPreviewUtils';
import './ProcessDetail/ProcessDetailContent.css';

function DocxPreviewBody({ blob }) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container || !blob) return undefined;

    setLoading(true);
    setError('');
    container.innerHTML = '';

    renderAsync(blob, container, null, {
      className: 'seal-approval-docx-preview',
      inWrapper: true
    })
      .catch((err) => {
        if (!cancelled) setError(friendlyDocxPreviewError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [blob]);

  return (
    <div className="seal-approval-docx-wrap">
      {loading && (
        <div className="seal-approval-preview-loading">
          <Spin tip="正在加载 Word 预览..." />
        </div>
      )}
      {error && (
        <div className="seal-approval-preview-fallback">
          <p>{error}</p>
        </div>
      )}
      <div ref={containerRef} className="seal-approval-docx-container" />
    </div>
  );
}

function extractTableHtml(html) {
  const match = String(html || '').match(/<table[\s\S]*<\/table>/i);
  return match ? match[0] : '';
}

const EXCEL_PREVIEW_CSS = `
.excel-preview-wrap { max-height: 70vh; overflow: auto; }
.excel-preview-table { overflow: auto; }
.excel-preview-table table { border-collapse: collapse; font-size: 12px; font-family: Arial, "Microsoft YaHei", sans-serif; }
.excel-preview-table td, .excel-preview-table th { border: 1px solid #d9d9d9; padding: 3px 8px; white-space: nowrap; color: #262626; }
.excel-preview-table tr:nth-child(even) td { background: #fafafa; }
`;

export function ExcelPreviewBody({ blob }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sheets, setSheets] = useState([]);
  const [active, setActive] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!blob) return undefined;

    setLoading(true);
    setError('');
    setSheets([]);

    blob.arrayBuffer()
      .then((buffer) => {
        if (cancelled) return;
        const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array', cellDates: true });
        const names = workbook.SheetNames || [];
        const list = names.map((name) => {
          let html = '';
          try {
            html = extractTableHtml(XLSX.utils.sheet_to_html(workbook.Sheets[name], { editable: false }));
          } catch {
            html = '';
          }
          return { name, html };
        });
        setSheets(list);
        setActive(list[0]?.name || '');
      })
      .catch(() => {
        if (!cancelled) setError('Excel 解析失败，请下载查看');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [blob]);

  if (loading) {
    return (
      <div className="seal-approval-preview-loading">
        <Spin tip="正在加载 Excel 预览..." />
      </div>
    );
  }
  if (error) {
    return (
      <div className="seal-approval-preview-fallback">
        <p>{error}</p>
      </div>
    );
  }
  if (!sheets.length) {
    return (
      <div className="seal-approval-preview-fallback">
        <p>工作簿中没有可预览的工作表</p>
      </div>
    );
  }

  const activeSheet = sheets.find((s) => s.name === active) || sheets[0];

  return (
    <div className="excel-preview-wrap">
      <style>{EXCEL_PREVIEW_CSS}</style>
      {sheets.length > 1 && (
        <Tabs
          size="small"
          activeKey={activeSheet.name}
          onChange={setActive}
          items={sheets.map((s) => ({ key: s.name, label: s.name }))}
        />
      )}
      {activeSheet.html ? (
        <div
          className="excel-preview-table"
          dangerouslySetInnerHTML={{ __html: activeSheet.html }}
        />
      ) : (
        <div className="seal-approval-preview-fallback">
          <p>（该工作表为空）</p>
        </div>
      )}
    </div>
  );
}

function PreviewFallback({ kind, downloadUrl, fileName }) {
  let message = '该文件格式暂不支持在线预览，请下载查看';
  if (kind === 'legacy-docx-disguised') {
    message = '该文件扩展名为 .docx，但实际是旧版 Word（.doc）格式。请在预付款申请页重新下载「无票承诺书模板」，用 Word 打开后另存为 .docx 再上传，或直接上传 PDF/图片扫描件。';
  } else if (kind === 'legacy-doc') {
    message = '旧版 Word（.doc）格式浏览器无法直接预览，请下载后用 Word 打开，或上传 .docx / PDF / 图片';
  }

  return (
    <div className="seal-approval-preview-fallback">
      <p>{message}</p>
      <Button type="link" href={downloadUrl} download={fileName} target="_blank" rel="noreferrer">
        下载查看
      </Button>
    </div>
  );
}

export default function AttachmentPreviewModal({ item, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [kind, setKind] = useState(null);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState('');

  useEffect(() => {
    if (!open || !item?.path) {
      setLoading(false);
      setError('');
      setKind(null);
      setBlob(null);
      setBlobUrl('');
      return undefined;
    }

    let cancelled = false;
    let objectUrl = '';

    setLoading(true);
    setError('');
    setKind(null);
    setBlob(null);
    setBlobUrl('');

    fetch(resolveUploadUrl(item.path))
      .then(async (res) => {
        if (!res.ok) throw new Error('文件加载失败');
        const fetchedBlob = await res.blob();
        if (cancelled) return;
        const bytes = await readBlobHead(fetchedBlob);
        const detected = sniffPreviewKindFromBytes(bytes, item.name);
        objectUrl = URL.createObjectURL(fetchedBlob);
        setBlob(fetchedBlob);
        setBlobUrl(objectUrl);
        setKind(detected);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || '文件加载失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, item?.path, item?.name]);

  if (!open || !item) return null;

  const modalWidth = kind === 'pdf' || kind === 'docx' || kind === 'excel' ? 920 : 760;

  return (
    <Modal
      title={item.name || '附件预览'}
      open={open}
      onCancel={onClose}
      footer={[
        blobUrl ? (
          <Button key="download" href={blobUrl} download={item.name} target="_blank" rel="noreferrer">
            下载
          </Button>
        ) : null,
        <Button key="close" onClick={onClose}>关闭</Button>
      ].filter(Boolean)}
      width={modalWidth}
      destroyOnHidden
      centered
    >
      {loading && (
        <div className="seal-approval-preview-loading">
          <Spin tip="正在加载附件..." />
        </div>
      )}
      {!loading && error && (
        <div className="seal-approval-preview-fallback">
          <p>{error}</p>
        </div>
      )}
      {!loading && !error && kind === 'image' && blobUrl && (
        <div className="seal-approval-preview-body">
          <Image src={blobUrl} alt={item.name} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
        </div>
      )}
      {!loading && !error && kind === 'pdf' && blobUrl && (
        <iframe title={item.name} src={blobUrl} className="seal-approval-preview-pdf" />
      )}
      {!loading && !error && kind === 'docx' && blob && (
        <DocxPreviewBody blob={blob} />
      )}
      {!loading && !error && kind === 'excel' && blob && (
        <ExcelPreviewBody blob={blob} />
      )}
      {!loading && !error && (kind === 'legacy-doc' || kind === 'legacy-docx-disguised' || kind === 'unknown') && blobUrl && (
        <PreviewFallback kind={kind} downloadUrl={blobUrl} fileName={item.name} />
      )}
    </Modal>
  );
}
